import React, { useCallback, useEffect, useState } from 'react';
import ScreeningAlerts from './ScreeningAlerts';
import { ApiSanctionAlert, ApiSanctionEntityType, sanctionAlertsApi } from '../../apis';
import { InternalApiScreeningAlertStatus } from '../../api-internal';
import { useAlertStatuses } from '../../screening/alert-status/alertStatusesHelper';
import { systemAlertStatusCodes } from '../../screening/alert-status/alertStatusesConstants';
import { useMe } from '../../organisation/useMe';

interface PersonScreeningAlertsProps {
  personId: string;
  entityType?: ApiSanctionEntityType;
}

export enum AlertFilterStatusOperation {
  IS = 'Is',
  IS_NOT = 'Is not',
}

export const PersonScreeningAlerts = ({ personId, entityType }: Props) => {
  const [fetchingScreeningAlerts, setFetchingScreeningAlerts] = useState(true);
  const size = 10;
  const [page, setPage] = useState<number>(0);
  const [failedToLoad, setFailedToLoad] = useState(false);
  const [screeningAlerts, setScreeningAlerts] = useState<ApiSanctionAlert[]>([]);
  const [total, setTotal] = useState<number>(0);
  const { isLoading: isCurrentUserDataLoading } = useMe();
  const [activeStatuses, statuses, , , hasFetchedStatuses] = useAlertStatuses();
  const [selectedStatuses, setSelectedStatuses] = useState<InternalApiScreeningAlertStatus[] | null>(null);
  const [operation, setOperation] = useState<AlertFilterStatusOperation>(AlertFilterStatusOperation.IS_NOT);

  useEffect(() => {
    if (isCurrentUserDataLoading || !hasFetchedStatuses) {
      return;
    }
    setSelectedStatuses(
      activeStatuses
        .filter((status) => status.enabledScreening)
        .filter((status) => status.code === systemAlertStatusCodes.filtered)
    );
  }, [isCurrentUserDataLoading, hasFetchedStatuses, activeStatuses]);

  const getStatuses = useCallback(
    (
      statuses: InternalApiScreeningAlertStatus[],
      selectedStatuses: InternalApiScreeningAlertStatus[],
      operation: AlertFilterStatusOperation
    ) => {
      if (operation === AlertFilterStatusOperation.IS) {
        return selectedStatuses;
      } else {
        const selectedStatusCodes = selectedStatuses?.map((status) => status.code) ?? [];
        return statuses.filter((status) => !selectedStatusCodes.includes(status.code));
      }
    },
    []
  );

  useEffect(() => {
    if (selectedStatuses === null) {
      return;
    }
    setFetchingScreeningAlerts(true);
    sanctionAlertsApi
      .findAllSanctionAlerts({
        personId,
        page,
        size,
        entityType,
        statuses: getStatuses(statuses, selectedStatuses, operation).map((status) => status.code),
      })
      .then((alerts) => {
        setFailedToLoad(false);
        setScreeningAlerts(alerts.content);
        if (entityType) {
          setTotal(page * size + alerts.total);
        } else {
          setTotal(alerts.total);
        }
      })
      .catch(() => {
        setFailedToLoad(true);
      })
      .finally(() => setFetchingScreeningAlerts(false));
  }, [personId, page, size, entityType, statuses, selectedStatuses, operation, getStatuses]);

  const onStatusUpdate = useCallback((statuses: InternalApiScreeningAlertStatus[]) => {
    setPage(0);
    setSelectedStatuses(statuses);
  }, []);

  return (
    <ScreeningAlerts
      page={page}
      size={size}
      entityType={entityType}
      total={total}
      alerts={screeningAlerts}
      onUpdatePage={setPage}
      selectedOperation={operation}
      onUpdateOperation={setOperation}
      selectedStatuses={selectedStatuses ?? []}
      onUpdateStatuses={onStatusUpdate}
      expandable={true}
      loading={fetchingScreeningAlerts}
      failedToLoad={failedToLoad}
      loadMorePaginated={!!entityType}
    />
  );
};

type Props = PersonScreeningAlertsProps;

export default PersonScreeningAlerts;
